<template>
  <BaseModal :modalTitle="$t('W: Bemærkninger')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <RichTextEditor v-model="text" />

      <div
        v-if="groupedItems.length > 0"
        class="mt-4 border-t border-border-subtle pt-4">
        <span class="mb-3 block text-sm font-medium text-text-primary">
          {{ $t('W: Indsæt fra beskrivelse') }}
        </span>

        <!-- List tabs -->
        <div class="mb-3 flex gap-1.5 overflow-x-auto pb-0.5">
          <button
            v-for="group in groupedItems"
            :key="group.descriptionID"
            type="button"
            class="shrink-0 cursor-pointer rounded-full px-3 py-1 text-xs font-medium transition-colors"
            :class="
              activeDescriptionID === group.descriptionID
                ? 'bg-action text-white'
                : 'bg-surface-muted text-text-secondary hover:bg-surface-sunken'
            "
            @click="onSelectList(group.descriptionID)">
            {{ group.descriptionName }}
          </button>
        </div>

        <!-- Search -->
        <input
          v-model="searchQuery"
          type="text"
          class="mb-3 w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-muted focus:border-action focus:ring-1 focus:ring-action"
          :placeholder="$t('W: Søg...')" />

        <!-- Items -->
        <div class="flex max-h-48 flex-col divide-y divide-border-subtle overflow-y-auto rounded-lg border border-border-subtle">
          <div
            v-if="filteredItems.length === 0"
            class="p-4 text-center text-sm text-text-muted">
            {{ $t('W: Ingen elementer') }}
          </div>
          <button
            v-for="item in filteredItems"
            :key="item.id"
            type="button"
            class="flex cursor-pointer items-start px-3 py-2.5 text-left hover:bg-surface-muted"
            @click="appendToEditor(item.getText(reportLocale))">
            <div class="flex min-w-0 flex-col">
              <span class="text-sm text-text-primary">{{ item.getTitle(reportLocale) }}</span>
              <span
                v-if="stripHtml(item.getText(reportLocale))"
                class="line-clamp-1 text-xs text-text-secondary">
                {{ stripHtml(item.getText(reportLocale)) }}
              </span>
            </div>
          </button>
        </div>
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.cancel')"
          theme="neutral"
          @click="$emit('close')" />
        <UiBaseButton
          :text="$t('action.save')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { mapState, mapActions } from 'pinia'
import { useAppStore } from '@/stores/appStore'
import { useOrderStore } from '@/stores/orderStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'
import type { ReportDescriptionItem } from '@/stores/reportDescriptionItemStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { sanitizeRichText } from '@/globals/typescript/utils/sanitizeRichText'
import { isRichTextEmpty } from '@/globals/typescript/utils/isRichTextEmpty'

interface IGroupedItems {
  descriptionID: string
  descriptionName: string
  items: ReportDescriptionItem[]
}

export default {
  name: 'OrderReportOrderDescriptionModal',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
    tatGroupID: { type: String, required: true },
    reportTemplateID: { type: String, required: true },
    currentText: { type: String, required: true },
    reportLocale: { type: String, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      text: this.currentText,
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
      activeDescriptionID: null as string | null,
      searchQuery: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),
    ...mapState(useReportDescriptionStore, ['allReportDescriptions']),
    ...mapState(useReportDescriptionItemStore, ['allReportDescriptionItems']),

    groupedItems(): IGroupedItems[] {
      const groups: IGroupedItems[] = []
      for (const description of this.allReportDescriptions) {
        if (description.activeState !== 'active') continue
        if (description.options?.usesDescriptions === false) continue
        const items = this.allReportDescriptionItems.filter(
          (x) =>
            x.reportDescriptionID === description.id && x.activeState === 'active'
        )
        if (items.length === 0) continue
        groups.push({
          descriptionID: description.id,
          descriptionName: description.getName(this.reportLocale),
          items,
        })
      }
      return groups.sort((a, b) => a.descriptionName.localeCompare(b.descriptionName))
    },

    activeGroup(): IGroupedItems | null {
      return (
        this.groupedItems.find((x) => x.descriptionID === this.activeDescriptionID) ?? null
      )
    },

    filteredItems(): ReportDescriptionItem[] {
      if (!this.activeGroup) return []
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return this.activeGroup.items
      return this.activeGroup.items.filter((item) =>
        item.getTitle(this.reportLocale).toLowerCase().includes(query)
      )
    },
  },

  watch: {
    groupedItems(groups: IGroupedItems[]) {
      if (!this.activeDescriptionID && groups.length > 0) {
        this.activeDescriptionID = groups[0]?.descriptionID ?? null
      }
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['updateOrderReportOrderDescriptions']),

    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.updateOrderReportOrderDescriptions(
          this.orderID,
          this.tatGroupID,
          this.reportTemplateID,
          sanitizeRichText(this.text)
        )
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Bemærkninger gemt'))
        this.$emit('close')
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke gemme bemærkninger'))
      } finally {
        this.isLoading = false
      }
    },

    appendToEditor(html: string): void {
      this.text = isRichTextEmpty(this.text) ? html : this.text + html
    },

    onSelectList(descriptionID: string): void {
      this.activeDescriptionID = descriptionID
      this.searchQuery = ''
    },

    stripHtml(html: string): string {
      const tmp = document.createElement('div')
      tmp.innerHTML = html
      return tmp.textContent ?? ''
    },
  },

  async created(): Promise<void> {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    // Registered before the awaits below so the baseline is the incoming
    // currentText; only `text` is a draft, the rest of data() is browsing state.
    this.unsavedGuard.register({
      snapshot: () => ({ text: this.text }),
      modalId: useModalStore().top?.id,
    })

    await Promise.all([
      useReportDescriptionStore().getReportDescriptions(),
      useReportDescriptionItemStore().getReportDescriptionItems(),
    ])
    if (this.groupedItems.length > 0) {
      this.activeDescriptionID = this.groupedItems[0]?.descriptionID ?? null
    }
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
